import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { NgOptimizedImage, UpperCasePipe } from '@angular/common';

import { type EntityLanguage } from '../../models/entity-language.model';
import { entityLanguageOptions } from '../../utils/language.utils';

type LanguagePickerAppearance = 'inline' | 'cards';

/**
 * Canonical language picker. One component, two appearances so every
 * modal / wizard that lets the user pick a content language speaks the
 * same visual vocabulary (flag + code + optional name).
 *
 *   • `inline` — compact segmented toggle. Fits inside a wizard section
 *     header row, next to a "Language" screen-reader label. Retires the
 *     old `.language-pills` CSS-only pattern that shipped without flags.
 *
 *   • `cards`  — 2-column grid of tap-target cards. Used as a dedicated
 *     wizard step (create-task-modal, org-selector-modal) where the
 *     language decision deserves its own screen.
 *
 * The workspace-identity account menu language switch is intentionally
 * NOT built on this primitive — it's a different UX (settings-menu
 * item, not a content picker) and stays inline in that overlay.
 *
 * The import-assistant onboarding step has bespoke per-language
 * descriptions and stays inline until that pattern spreads or a
 * description slot is worth adding.
 */
@Component({
  selector: 'app-language-picker',
  templateUrl: './language-picker.html',
  styleUrl: './language-picker.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage, UpperCasePipe],
})
export class LanguagePickerComponent {
  /** Two-way binding — use `[(language)]="signal"`. Null on init before
   *  the caller has resolved the org's default content language. */
  readonly language = model<EntityLanguage | null>(null);
  /** Visual variant — see the class docstring for when to pick which. */
  readonly appearance = input<LanguagePickerAppearance>('inline');
  /** Locks all buttons at once. The value stays visible so the user can
   *  read the current state during read-only reviews. */
  readonly disabled = input(false);

  protected readonly options = entityLanguageOptions();

  protected select(value: EntityLanguage): void {
    if (this.disabled()) return;
    this.language.set(value);
  }
}
